<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useCounterStore } from '../stores/counter'
import { CAMPAIGN_CONFIG } from '../utils/config'
import { preloadCarAssets } from '../utils/carAssets'
import { LIGHT_ON_AT, PLACE_NUMBER_AT } from '../utils/timing'
import qLogo from '../assets/q-logo.webp'
import NeonDigit from './NeonDigit.ce.vue'
import ThxBackdrop from './ThxBackdrop.ce.vue'
import ThxCar from './ThxCar.ce.vue'
import ThxSmoke from './ThxSmoke.ce.vue'

// The first fetch is tried a few times, so a short hiccup does not leave the tile empty.
const FIRST_FETCH_ATTEMPTS = 3
const FIRST_FETCH_RETRY_MS = 3000

const store = useCounterStore()
const root = ref(null)
let refreshTimer
let placeTimer
let lightTimer
let observer
let disposed = false

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
// The scene (car and smoke) only starts when the tile is in view and the car has loaded.
const started = ref(false)
const introDone = ref(reducedMotion)
const lightOn = ref(reducedMotion)
// All attempts to fetch the first number failed.
const gaveUp = ref(false)

const unavailable = computed(() => store.total === null && store.failed && gaveUp.value)
const formatted = computed(() => {
  if (unavailable.value) {
    return '---'
  }
  return store.total === null ? '' : String(store.total).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
})
const spoken = computed(() => (unavailable.value ? 'Niet beschikbaar' : formatted.value))
const cells = computed(() =>
  [...formatted.value].map((char) =>
    char === '.' || char === '-' ? { symbol: char, digit: 0 } : { symbol: null, digit: Number(char) }
  )
)
const dots = computed(() => cells.value.filter((cell) => cell.symbol === '.').length)
const shown = computed(() => (store.total !== null || unavailable.value) && introDone.value)
const lit = computed(() => shown.value && lightOn.value)

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

async function fetchFirst() {
  for (let attempt = 0; attempt < FIRST_FETCH_ATTEMPTS && !disposed; attempt++) {
    if (await store.fetchCounter()) {
      return
    }
    await wait(FIRST_FETCH_RETRY_MS)
  }
  gaveUp.value = true
}

function whenVisible(element) {
  if (!('IntersectionObserver' in window)) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect()
          resolve()
        }
      },
      { threshold: 0.4 }
    )
    observer.observe(element)
  })
}

async function startScene() {
  await Promise.all([whenVisible(root.value), preloadCarAssets()])
  if (disposed) {
    return
  }
  started.value = true
  if (!introDone.value) {
    placeTimer = setTimeout(() => (introDone.value = true), PLACE_NUMBER_AT * 1000)
    lightTimer = setTimeout(() => (lightOn.value = true), LIGHT_ON_AT * 1000)
  }
}

onMounted(() => {
  fetchFirst()
  refreshTimer = setInterval(store.fetchCounter, CAMPAIGN_CONFIG.refreshIntervalMs)
  startScene()
})

onBeforeUnmount(() => {
  disposed = true
  observer?.disconnect()
  clearInterval(refreshTimer)
  clearTimeout(placeTimer)
  clearTimeout(lightTimer)
})
</script>

<template>
  <section ref="root" class="thx-counter">
    <ThxBackdrop />
    <template v-if="started">
      <ThxSmoke />
      <ThxCar />
    </template>

    <p
      class="thx-counter__number"
      :class="{ 'thx-counter__number--shown': shown }"
      :style="{ '--chars': Math.max(formatted.length - dots * 0.5, 3) }"
      aria-live="polite"
    >
      <span class="thx-counter__sr">{{ spoken }}</span>
      <!-- Keyed from the right, so existing digits keep their state when the number gets a digit more. -->
      <NeonDigit
        v-for="(cell, index) in cells"
        :key="cells.length - index"
        :digit="cell.digit"
        :symbol="cell.symbol"
        :index="index"
        :lit="lit"
        :rollIn="introDone"
      />
    </p>

    <!-- The plate of the car stays behind under the number. -->
    <div
      class="thx-counter__plate"
      :class="{ 'thx-counter__plate--shown': lit, 'thx-counter__plate--lit': lit }"
      :style="{ '--i': formatted.length }"
      aria-hidden="true"
    >
      <div class="thx-counter__plate-side">
        <img class="thx-counter__plate-q" :src="qLogo" alt="" />
      </div>
      <span class="thx-counter__plate-text">THX TO YOU TOUR</span>
    </div>
  </section>
</template>

<style scoped>
:host {
  display: block;
}

:host {
  display: block;
  width: 100%;
  aspect-ratio: 32 / 9;
}

.thx-counter {
  --q-red: 237 54 36;

  position: relative;
  overflow: clip;
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 32 / 9;
  border-radius: 1.5cqw;
  background: #6c0000;
  color: #fff;
  container-type: inline-size;
  isolation: isolate;
}

/* Neon digits sized to the tile width, leaving 5% padding on each side; a digit cell is 0.4em wide. */
.thx-counter__number {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  margin: 0;
  min-height: 1em;
  font-family: 'Cervo', sans-serif;
  font-size: min(calc(45cqw / (var(--chars, 6) * 0.4)), 19.5cqw);
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  text-transform: uppercase;
  opacity: 0;
  transition: opacity 0.15s linear;
}

.thx-counter__number--shown {
  opacity: 1;
}

@keyframes thx-neon-ignite {
  0% {
    opacity: 0;
  }

  6% {
    opacity: 0.9;
  }

  8% {
    opacity: 0;
  }

  15% {
    opacity: 0;
  }

  16% {
    opacity: 1;
  }

  19%,
  24% {
    opacity: 0.15;
  }

  26% {
    opacity: 1;
  }

  30% {
    opacity: 0.45;
  }

  34%,
  55% {
    opacity: 1;
  }

  57% {
    opacity: 0.7;
  }

  59%,
  100% {
    opacity: 1;
  }
}

@keyframes thx-neon-idle {
  0%,
  86%,
  100% {
    opacity: 1;
  }

  87% {
    opacity: 0.2;
  }

  88% {
    opacity: 1;
  }

  89% {
    opacity: 0.5;
  }

  91% {
    opacity: 1;
  }
}

.thx-counter__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* A real-looking plate, hanging straight under the number. The neon glow behind it lights up with the digits. */
.thx-counter__plate {
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: 3.3cqw;
  translate: -50% 0;
  display: flex;
  align-items: stretch;
  height: 2.5cqw;
  border: 0.09cqw solid #1d1d1d;
  border-radius: 0.35cqw;
  background: linear-gradient(180deg, #fbfaf4 0%, #e9e7dc 100%);
  box-shadow:
    0 0.25cqw 0.45cqw rgb(0 0 0 / 55%),
    inset 0 0 0.175cqw rgb(0 0 0 / 25%);
  opacity: 0;
  transition: opacity 0.15s linear;
}

.thx-counter__plate--shown {
  opacity: 1;
}

/* Dark when the neon is off, then flickers on with the digits. It covers the border too, so no corner stays light. */
.thx-counter__plate::before {
  content: '';
  position: absolute;
  inset: -0.09cqw;
  z-index: 2;
  border-radius: 0.35cqw;
  background: rgb(60 0 0 / 55%);
  pointer-events: none;
}

.thx-counter__plate::after {
  content: '';
  position: absolute;
  inset: -0.09cqw;
  z-index: -1;
  border-radius: inherit;
  box-shadow:
    0 0 0.7cqw rgb(var(--q-red) / 90%),
    0 0 1.8cqw rgb(var(--q-red) / 55%);
  opacity: 0;
}

.thx-counter__plate--lit::before {
  animation:
    thx-plate-dim-ignite 1.6s linear calc(var(--i) * 0.17s) both,
    thx-plate-dim-idle 11s linear calc(var(--i) * 0.17s + 1.6s) infinite;
}

.thx-counter__plate--lit::after {
  animation:
    thx-neon-ignite 1.6s linear calc(var(--i) * 0.17s) both,
    thx-neon-idle 11s linear calc(var(--i) * 0.17s + 1.6s) infinite;
}

.thx-counter__plate-side {
  display: grid;
  place-items: center;
  width: 2.6cqw;
  border-radius: 0.26cqw 0 0 0.26cqw;
  background: linear-gradient(180deg, #ee1c2c 0%, #c4091a 100%);
  box-shadow: inset -0.06cqw 0 0.125cqw rgb(0 0 0 / 30%);
}

.thx-counter__plate-q {
  width: 1.45cqw;
  height: auto;
}

.thx-counter__plate-text {
  align-self: center;
  padding: 0 1.1cqw 0 1cqw;
  font-family: 'Cervo', sans-serif;
  font-size: 1.8cqw;
  line-height: 1;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: #141414;
  -webkit-text-stroke: 0.07cqw #141414;
  text-shadow: 0 0.06cqw 0 rgb(255 255 255 / 70%);
}

@keyframes thx-plate-dim-ignite {
  0% {
    opacity: 1;
  }

  6% {
    opacity: 0.1;
  }

  8%,
  15% {
    opacity: 1;
  }

  16% {
    opacity: 0;
  }

  19%,
  24% {
    opacity: 0.85;
  }

  26% {
    opacity: 0;
  }

  30% {
    opacity: 0.55;
  }

  34%,
  55% {
    opacity: 0;
  }

  57% {
    opacity: 0.3;
  }

  59%,
  100% {
    opacity: 0;
  }
}

@keyframes thx-plate-dim-idle {
  0%,
  86%,
  100% {
    opacity: 0;
  }

  87% {
    opacity: 0.8;
  }

  88% {
    opacity: 0;
  }

  89% {
    opacity: 0.5;
  }

  91% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thx-counter__number,
  .thx-counter__plate {
    transition: none;
  }

  .thx-counter__plate--lit::after {
    animation: none;
    opacity: 1;
  }

  .thx-counter__plate--lit::before {
    animation: none;
    opacity: 0;
  }
}
</style>
